import Button from '@jetbrains/ring-ui/components/button/button'
import Input from '@jetbrains/ring-ui/components/input/input'
import type {SelectItem} from '@jetbrains/ring-ui/components/select/select'
import Select from '@jetbrains/ring-ui/components/select/select'
import Tooltip from '@jetbrains/ring-ui/components/tooltip/tooltip'
import classNames from 'classnames'
import * as React from 'react'
import type {PreloadedQuery} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'

import type {AgentPool} from '../../../../../../services/rest'
import {restApi} from '../../../../../../services/rest'
import type {AgentPoolId, AgentId} from '../../../../../../types'
import {stringifyId} from '../../../../../../types'
import {notNull} from '../../../../../../utils/guards'
import type {
  AgentsPagesPoolAuthorizePermissionsQuery,
  AgentsPagesPoolAuthorizePermissionsQuery$data,
} from '../../../__generated__/AgentsPagesPoolAuthorizePermissionsQuery.graphql'
import type {AgentsPagesPoolsAuthorizePermissionsQuery} from '../../../__generated__/AgentsPagesPoolsAuthorizePermissionsQuery.graphql'
import {poolAuthorizePermissionsQuery} from '../../../AgentsPages.queries'
import CommonForm from '../../CommonForm/CommonForm'
import CommonPopup from '../../CommonPopup/CommonPopup'

import {useCanAuthorizeInPools, usePoolsAuthorizePermissionsQueryRef} from './AuthorizeAgent.hooks'

import styles from './AuthorizeAgent.css'

type Props = {
  id: AgentId
  className?: string
  name: string
  poolId?: AgentPoolId
  enabled?: boolean
}
type AgentPoolOption = {
  value?: AgentPoolId
}

function poolsToOptions(pools: ReadonlyArray<AgentPool>) {
  return pools.filter(notNull).map(
    ({id, name}): SelectItem<AgentPoolOption> => ({
      key: name ?? '',
      label: name,
      value: id,
    }),
  )
}

export function AuthorizeAgentContainer(props: Props) {
  const [poolsAuthorizePermissionsQueryRef] = usePoolsAuthorizePermissionsQueryRef()

  return poolsAuthorizePermissionsQueryRef != null ? (
    <AuthorizeAgentDataContainer queryRef={poolsAuthorizePermissionsQueryRef} {...props} />
  ) : null
}

function AuthorizeAgentDataContainer({
  queryRef,
  ...props
}: Props & {queryRef: PreloadedQuery<AgentsPagesPoolsAuthorizePermissionsQuery>}) {
  const {canAuthorizeInPools, isAgentLeft} = useCanAuthorizeInPools(queryRef, props.poolId)

  return canAuthorizeInPools.length > 0 ? (
    <AuthorizeAgent
      {...props}
      authorized={false}
      isAgentLeft={isAgentLeft}
      authorizableInPools={canAuthorizeInPools}
    />
  ) : null
}

export function UnauthorizeAgentContainer(props: Props) {
  const [
    poolAuthorizePermissionsQueryRef,
    loadPoolAuthorizePermissionsQuery,
    disposePoolAuthorizePermissionsQuery,
  ] = useQueryLoader<AgentsPagesPoolAuthorizePermissionsQuery>(poolAuthorizePermissionsQuery)

  React.useEffect(() => {
    loadPoolAuthorizePermissionsQuery(
      {agentPoolId: stringifyId(props.poolId)},
      {fetchPolicy: 'store-and-network'},
    )

    return disposePoolAuthorizePermissionsQuery
  }, [disposePoolAuthorizePermissionsQuery, loadPoolAuthorizePermissionsQuery, props.poolId])

  return poolAuthorizePermissionsQueryRef != null ? (
    <UnauthorizeAgentDataContainer queryRef={poolAuthorizePermissionsQueryRef} {...props} />
  ) : null
}

function UnauthorizeAgentDataContainer({
  queryRef,
  ...props
}: Props & {queryRef: PreloadedQuery<AgentsPagesPoolAuthorizePermissionsQuery>}) {
  const {agentPool}: AgentsPagesPoolAuthorizePermissionsQuery$data = usePreloadedQuery(
    poolAuthorizePermissionsQuery,
    queryRef,
  )

  return agentPool?.permissions?.authorizeAgents ? <AuthorizeAgent {...props} authorized /> : null
}

export default function AuthorizeAgent({
  id,
  className,
  name,
  poolId,
  authorized,
  isAgentLeft,
  authorizableInPools,
}: Props & {
  authorized?: boolean
  isAgentLeft?: boolean
  authorizableInPools?: ReadonlyArray<AgentPool>
}) {
  const poolOptions = authorizableInPools != null ? poolsToOptions(authorizableInPools) : undefined
  const [show, setShow] = React.useState(false)
  const [comment, setComment] = React.useState('')
  const [poolIdToAuthorize, setPoolIdToAuthorize] = React.useState<number | null | undefined>(null)
  const formRef = React.useRef()
  const [changeAgentAuthState] = restApi.endpoints.setAuthorizedInfo.useMutation()
  const [assignAgentWithPool] = restApi.endpoints.setAgentPool.useMutation()

  const handleSubmit = React.useCallback(async () => {
    const authorizePayload = {
      comment: {
        text: comment,
      },
      status: authorized !== true,
    }

    const agentLocator = `id:${id}`
    if (poolIdToAuthorize != null) {
      await assignAgentWithPool({
        agentLocator,
        agentPool: {id: poolIdToAuthorize},
        fields: 'id,name',
      })
    }

    await changeAgentAuthState({agentLocator, authorizedInfo: authorizePayload})
  }, [comment, authorized, id, poolIdToAuthorize, changeAgentAuthState, assignAgentWithPool])
  const handleSuccess = React.useCallback(() => {
    setComment('')
    setPoolIdToAuthorize(null)
    setShow(false)
  }, [setShow])
  const handleToggleClick = React.useCallback(() => setShow(!show), [show, setShow])
  const handleCommentChange = React.useCallback(
    (e: React.SyntheticEvent<HTMLTextAreaElement>) => setComment(e.currentTarget.value),
    [setComment],
  )
  const handleSelectPool = React.useCallback(
    (option: SelectItem<AgentPoolOption> | null) => setPoolIdToAuthorize(option?.value),
    [setPoolIdToAuthorize],
  )
  const onClose = React.useCallback(() => setShow(false), [setShow])
  const classes = classNames(styles.authorizeAgent, className)
  const actionTitle = authorized === true ? 'Unauthorize' : 'Authorize'

  const selectedPoolId = poolIdToAuthorize ?? poolId

  const authorizeButton = React.useMemo(
    () => (
      <Button
        data-test-authorize-agent
        onClick={handleToggleClick}
        disabled={!authorized && !isAgentLeft}
      >
        {actionTitle}
        {'...'}
      </Button>
    ),
    [handleToggleClick, actionTitle, authorized, isAgentLeft],
  )

  return (
    <div className={classes}>
      {!authorized && !isAgentLeft ? (
        <Tooltip title={'No agent licenses left'}>
          <div>{authorizeButton}</div>
        </Tooltip>
      ) : (
        <>
          {authorizeButton}
          <CommonPopup
            hidden={!show}
            onClose={onClose}
            header={`${actionTitle} ${name}`}
            content={
              <CommonForm
                submitButtonContent={actionTitle}
                formRef={formRef}
                onSubmit={handleSubmit}
                onSuccess={handleSuccess}
                onClose={onClose}
                content={
                  <>
                    <Input
                      autoFocus
                      className={styles.comment}
                      inputClassName="submit-on-cmd-enter"
                      label="Comment"
                      placeholder="Add an explanation for colleagues"
                      multiline
                      value={comment}
                      onChange={handleCommentChange}
                    />
                    {authorized === false && poolOptions != null ? (
                      <Select
                        popupClassName={styles.poolSelectPopup}
                        selectedLabel="Associate with pool"
                        label="Associate with pool"
                        data={poolOptions}
                        selected={poolOptions.find(option => option.value === selectedPoolId)}
                        filter
                        onChange={handleSelectPool}
                      />
                    ) : null}
                  </>
                }
              />
            }
          />
        </>
      )}
    </div>
  )
}
